// src/pages/lkp/LookupReferenceStep.tsx
//
// Step 2 — ingest the client's authoritative reference (OPTIONAL). This is where
// client data actually loads:
//   1. upload the client value workbook (+ optional physical→logical dictionary)
//   2. Build authoritative — matches physical columns to client logical fields/values
//   3. review & confirm/remap each mapping (LookupReferenceReview: score, runner-up,
//      observed-vs-governed comparison)
//   4. Fold into entities — attaches the confirmed client values to the entities as
//      canonical evidence, then hands off to Step 3.
// No final code-value decisions here — that is Step 3.

import React, { useCallback, useEffect, useState } from "react";
import {
  lookup_getAuthoritative, lookup_getCandidates, lookup_getLogicalFields,
  lookup_uploadReference, lookup_uploadReferenceKind, lookup_buildAuthoritative,
  type AuthoritativeBlock, type LookupCandidatesBlock,
} from "../api/lkpAPI";
import { lookup_foldAuthoritative } from "../api/lkpEntityAPI";
import LookupReferenceReview from "./LookupReferenceReview";

interface Props {
  appNm: string; entityId: string;
  onGoDiscover: () => void;      // back to Step 1
  onFolded: () => void;          // continue to Step 3
}

export default function LookupReferenceStep({ appNm, entityId, onGoDiscover, onFolded }: Props) {
  const [auth, setAuth] = useState<AuthoritativeBlock | null>(null);
  const [cand, setCand] = useState<LookupCandidatesBlock | null>(null);
  const [logicalFields, setLogicalFields] = useState<string[]>([]);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [dictFile, setDictFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 4000); };

  const load = useCallback(async () => {
    const [a, c, lf] = await Promise.allSettled([
      lookup_getAuthoritative(entityId), lookup_getCandidates(entityId), lookup_getLogicalFields(entityId),
    ]);
    setAuth(a.status === "fulfilled" ? a.value.data : null);
    setCand(c.status === "fulfilled" ? c.value.data : null);
    setLogicalFields(lf.status === "fulfilled" ? lf.value.logical_fields : []);
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const build = async () => {
    setBusy("build");
    try {
      if (refFile) await lookup_uploadReference(appNm, refFile);
      if (dictFile) await lookup_uploadReferenceKind(appNm, "dictionary", dictFile);
      const r = await lookup_buildAuthoritative(appNm, entityId); setAuth(r.data);
      const lf = await lookup_getLogicalFields(entityId); setLogicalFields(lf.logical_fields);
      note("Authoritative mapping built. Review and confirm below.");
    } catch (e: any) { note(e?.detail || "Build failed", true); } finally { setBusy(null); }
  };

  const foldAndContinue = async () => {
    setBusy("fold");
    try { const r = await lookup_foldAuthoritative(entityId); note(`Folded client values into ${r.attached} field(s).`); onFolded(); }
    catch (e: any) { note(e?.detail || "Fold failed", true); } finally { setBusy(null); }
  };

  const built = !!auth?.definitions?.length;

  return (
    <div className="lrs">
      <style>{CSS}</style>

      {flash && <div className={"lrs-flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}

      <div className="lrs-intro">
        <b>Optional.</b> If the client governs these values already, load their workbook so its
        codes become canonical evidence. No client data? Skip to Step 3 and finalize from profiling.
      </div>

      <div className="lrs-grid">
        <div className="lrs-card">
          <div className="lrs-h">Client value workbook</div>
          <div className="lrs-d">Codes + descriptions the client governs (the LOV source).</div>
          <label className="lrs-file">{refFile ? refFile.name : "Choose workbook (.xlsx/.csv)"}
            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setRefFile(e.target.files?.[0] || null)} /></label>
        </div>
        <div className="lrs-card">
          <div className="lrs-h">Dictionary <span className="opt">optional</span></div>
          <div className="lrs-d">Physical → logical field names. Enables dictionary-first matching.</div>
          <label className="lrs-file opt">{dictFile ? dictFile.name : "Choose dictionary (.xlsx/.csv)"}
            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setDictFile(e.target.files?.[0] || null)} /></label>
        </div>
      </div>

      <div className="lrs-bar">
        <div className="lrs-bar-t"><b>Build authoritative mapping</b>
          <span>Match physical columns to the client's logical fields and value lists.</span></div>
        <button className="lrs-btn dark" disabled={!!busy || (!refFile && !built)} onClick={build}>
          {busy === "build" ? "Building…" : built ? "Rebuild" : "Build"}</button>
      </div>

      {built ? (
        <>
          <LookupReferenceReview
            entityId={entityId} auth={auth} candidates={cand} logicalFields={logicalFields}
            busy={!!busy} onSaved={load} onReconcile={foldAndContinue} onGoDiscover={onGoDiscover} />
          <div className="lrs-foot">
            <span>When mappings look right, fold the client values into the entities and finalize in Step 3.</span>
            <div className="lrs-sp" />
            <button className="lrs-btn dark" disabled={!!busy} onClick={foldAndContinue}>
              {busy === "fold" ? "Folding…" : "Fold into entities & continue →"}</button>
          </div>
        </>
      ) : (
        <div className="lrs-empty">Upload a workbook and Build to review mappings — or continue to Step 3 to finalize from profiling evidence.</div>
      )}
    </div>
  );
}

const CSS = `
.lrs{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--line2:#e9ede9;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--warn:#c23c28;--warn-bg:#f7e0da;--infer:#4a51c0;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx);font-size:14px}
.lrs *{box-sizing:border-box}
.lrs-flash{padding:9px 14px;border-radius:8px;font-size:13px;margin-bottom:12px;background:var(--gov-bg);color:var(--gov)}
.lrs-flash.err{background:var(--warn-bg);color:var(--warn)}
.lrs-intro{font-size:13px;color:var(--tx2);background:var(--surface2);border:1px solid var(--line);border-radius:10px;padding:11px 14px;margin-bottom:14px}
.lrs-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px}
.lrs-card{background:var(--surface);border:1px solid var(--line);border-radius:11px;padding:14px 15px}
.lrs-h{font-weight:600;font-size:13.5px}.lrs-h .opt{font-size:10.5px;color:var(--tx3);font-weight:500;margin-left:6px;text-transform:uppercase;letter-spacing:.05em}
.lrs-d{font-size:12px;color:var(--tx2);margin:3px 0 11px}
.lrs-file{display:block;border:1px dashed var(--line);border-radius:8px;padding:10px 12px;font-size:12.5px;color:var(--infer);cursor:pointer;text-align:center;background:var(--surface2)}
.lrs-file:hover{border-color:var(--infer)}.lrs-file.opt{color:var(--tx3)}
.lrs-bar{display:flex;align-items:center;gap:14px;background:var(--surface);border:1px solid var(--line);border-radius:11px;padding:13px 15px;margin-bottom:14px}
.lrs-bar-t{flex:1}.lrs-bar-t b{font-size:13.5px;display:block}.lrs-bar-t span{font-size:12.5px;color:var(--tx2)}
.lrs-btn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:9px 15px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.lrs-btn.dark{background:var(--ink);color:#fff;border-color:var(--ink)}.lrs-btn.dark:hover{background:#20303e}
.lrs-btn:disabled{opacity:.5;cursor:default}
.lrs-foot{display:flex;align-items:center;gap:11px;margin-top:16px;padding-top:14px;border-top:1px dashed var(--line);font-size:12.5px;color:var(--tx2);flex-wrap:wrap}
.lrs-sp{flex:1}
.lrs-empty{text-align:center;padding:34px;color:var(--tx3)}
@media(max-width:820px){.lrs-grid{grid-template-columns:1fr}}
`;
